/* eslint-disable @typescript-eslint/no-explicit-any */
import './DropDown.scss';
import React from "react";
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { IDropDownData, IDropDownTreeFilterData } from 'store/sidebar/types';
import { setQuickPlotThunk } from 'store/sidebar/thunk';
import { selectSelectedTab, workspaceSlice } from 'store/workspace/slice';
import { IElementType } from './types';
import { trackGAEvent } from 'helpers/functions/GoogleAnalytics/GA';
import { eventsGA } from 'helpers/functions/GoogleAnalytics/types';
import DropdownTreeFilter from 'components/dropdownTreeFilter';
import { transformArrayToTreeData } from 'components/dropdownTreeFilter/tree-data-operations';

// Extended interface that includes the 'id' property from the tree component
interface IExtendedDropDownTreeFilterData extends IDropDownTreeFilterData {
    id: string;
    items?: IExtendedDropDownTreeFilterData[];
    text: string;
}

export interface IDropDownProps {
    label?: string,
    items: IDropDownData[],
    element: IElementType,
    group: string | null,
    value: string | number | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFunction: (args: any) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    saveFunction: (element?: IElementType, group?: string | null) => any;
}

export const DropdownTreeFilterSidebar = ({ label, items, element, group, value, setFunction, saveFunction }: IDropDownProps) => {
    const dispatch = useAppDispatch();
    const [data, setData] = React.useState<IExtendedDropDownTreeFilterData[]>([]);
    const { plotId, workspaceId } = useAppSelector(state => state.workspaceSlice);
    const selectedTab = useAppSelector(selectSelectedTab)
    const [changed, setChanged] = React.useState<boolean>(false);

    React.useEffect(() => {
        // Cast the result to the extended interface type
        setData(transformArrayToTreeData(items.slice()) as IExtendedDropDownTreeFilterData[]);
    }, [items]);

    const handleChange = async (e: IExtendedDropDownTreeFilterData | null) => {
        // Handle null value case (when selection is cleared via close icon)
        if (!e) {
            await setFunction({ group, element, value: null });
            // If clearing xTagAttributeId, also clear xPropertyCalculatorParameterId
            if (element === 'xTagAttributeId') {
                await setFunction({ group, element: 'xPropertyCalculatorParameterId', value: null });
            }
            await saveFunction(element, group);
            setChanged(true);
            return;
        }
        
        if (element === 'quickPlotId') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { payload } : any = await dispatch(setQuickPlotThunk({
                workspaceId,
                plotId,
                quickPlotId: e.tagAttributeId,
                field: selectedTab?.type === 2 ? 'histogramOptions' : 'plotOptions'
            }))

            dispatch(workspaceSlice.actions.changePlotTitle({ id: plotId, value: payload?.data?.title}))
            return
        }
        if(element === 'xTagAttributeId'){
            trackGAEvent(eventsGA.plotXaxis, {xAxis: e.text})
            // Update tooltip[0] when X axis changes
            await setFunction({ group: 'serverSideOptions', element: 'tooltips', value: e, index: 0 })
            
            // Handle mutually exclusive fields: xTagAttributeId and xPropertyCalculatorParameterId
            if ((e as any).isCalculatorParameter) {
                // Set calculator parameter and clear tag attribute
                await setFunction({ group, element: 'xPropertyCalculatorParameterId', value: e.id })
                await setFunction({ group, element: 'xTagAttributeId', value: null })
            } else {
                // Set tag attribute and clear calculator parameter
                await setFunction({ group, element: 'xTagAttributeId', value: e.tagAttributeId || e.id })
                await setFunction({ group, element: 'xPropertyCalculatorParameterId', value: null })
            }
            await setChanged(true);
            return;
        }
        if(element === 'yTagAttributeId'){
            trackGAEvent(eventsGA.plotYaxis, {yAxis: e.text})
            // Update tooltip[1] when Y axis changes
            await setFunction({ group: 'serverSideOptions', element: 'tooltips', value: e, index: 1 })
        }
        await setFunction({ group, element, value: e.tagAttributeId || e.id })
        await setChanged(true);
        // await saveFunction(element, group);
    };

    React.useEffect(() => {
        if (changed) {
            saveFunction(element, group);
            setChanged(false);
        }
    }, [changed]);

    // Helper function to find an item in a nested tree structure
    const findItemInTree = (items: IExtendedDropDownTreeFilterData[], targetId: string | number | null | undefined): IExtendedDropDownTreeFilterData | undefined => {
        if (!items || !targetId) return undefined;
        
        for (const item of items) {
            // Check if current item matches
            if (item?.id === targetId || item?.tagAttributeId === targetId) {
                return item;
            }
            
            // Check in child items if they exist
            if (item.items && item.items.length > 0) {
                const found = findItemInTree(item.items, targetId);
                if (found) return found;
            }
        }
        return undefined;
    };

    // Look for selected item in the tree data
    let selected;
    
    if (value !== undefined && value !== null) {
        // First try to find using the value directly
        selected = findItemInTree(data, value);
        
        // If not found and data is available, do a more thorough search
        if (!selected && data.length > 0) {
            // Look through each group and all their items
            for (const group of data) {
                if (group.items && group.items.length > 0) {
                    // Try to match by either id or tagAttributeId
                    const match = group.items.find(item => 
                        item.id === value || 
                        item.tagAttributeId === value
                    );
                    if (match) {
                        selected = match;
                        break;
                    }
                }
            }
        }
    }
    
    return (
        <div className="dropDown">
            <label>{label || ''}</label>
            <DropdownTreeFilter value={selected || { text: 'Select' }} handleChange={handleChange} data={data}/>
        </div>
    );
};


interface ITooltipProps {
    label?: string,
    items: IDropDownData[],
    element: IElementType,
    group: string | null,
    value: any,
    index?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFunction: (args: any) => any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    saveFunction: (element?: IElementType, group?: string | null, index?: number) => any;
}

export const TooltipTreeFilter = ({ label, items, element, group, value, index, setFunction, saveFunction }: ITooltipProps) => {
    const [data, setData] = React.useState<IExtendedDropDownTreeFilterData[]>([]);
    const [changed, setChanged] = React.useState<boolean>(false);

    React.useEffect(() => {
        setData(transformArrayToTreeData(items.slice()) as IExtendedDropDownTreeFilterData[])
    }, [items]);

    const handleChange = (e: IExtendedDropDownTreeFilterData | null) => {
        if (!e) {
            setFunction({ group, element, value: null, index, data });
            setChanged(true);
            return;
        }
        trackGAEvent(eventsGA.map_tooltips, {tooltip: e?.text ?? ''})
        setFunction({ group, element, value: e, index, data })
        setChanged(true);
    };

    React.useEffect(() => {
        if (changed) {
            saveFunction(element, group, index);
            setChanged(false);
        }
    }, [changed, element, group, index, saveFunction]);

    const findItemInTree = (items: any[], targetId: string | number | null | undefined): IExtendedDropDownTreeFilterData | undefined => {
        if (!items || !targetId) return undefined;
        
        for (const item of items) {
            if (item?.id === targetId) {
                return item;
            }
            
            if (item.items && item.items.length > 0) {
                const found = findItemInTree(item.items, targetId);
                if (found) return found;
            }
        }
        return undefined;
    };

    let selected;
    
    if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
            // First try to find by tagAttributeId (which is the main ID stored in Redux)
            selected = findItemInTree(data, value?.tagAttributeId);
            
            // If not found, try by id as fallback
            if (!selected) {
                selected = findItemInTree(data, value?.id);
            }
            
            if (!selected && data.length > 0) {
                for (const group of data) {
                    if (group.items && group.items.length > 0) {
                        const match = group.items.find(item => 
                            item.id === value?.tagAttributeId || 
                            item.tagAttributeId === value?.tagAttributeId ||
                            item.id === value?.id || 
                            item.tagAttributeId === value?.id
                        );
                        if (match) {
                            selected = match;
                            break;
                        }
                    }
                }
            }
        } else {
            selected = findItemInTree(data, value);
        }
    }
    return (
        <div className="dropDown">
            <label>{label || ''}</label>
            <DropdownTreeFilter 
                value={selected || { text: 'Select' }} 
                handleChange={handleChange} 
                data={data}
            />
        </div>
    );
};